import { useLocalStorage } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, useBeforeUnload, useLocation, useMatches, useSearchParams } from 'react-router';

import { useLocationCtx } from '../../contexts';
import type { FilterValue, NextFilters, StoredFilters } from '../../types';
import { ObjectUtils, RouterUtils } from '../../utils';

interface FilterPropsOptions<T> {
  // Required as this method will be responsible to assign
  // required parameters and default values when the URL or
  // storage filters are not provided fully.
  parser: (filters: Partial<T>) => T;
  replace?: boolean;
  withPagination?: boolean;
  withLocalStorage?: boolean;
  withLocation?: boolean;
  scope?: string;
  onChange?: (oldFilter: Partial<T>, newFilter: Partial<T>, resetPage: boolean) => void;
}

export type FilterProps<T> = {
  current: T;
  updateFilters: (nextFilter?: NextFilters<T>, resetPage?: boolean, replaceSearch?: boolean) => void;
  changeFilter: (key: keyof T, value: FilterValue, resetPage?: boolean) => void;
  removeFilter: (key: keyof T, resetPage?: boolean) => void;
  removeMultipleFilters: (keys: (keyof T)[], resetPage?: boolean) => void;
  resetFilters: () => void;
};

export const FILTER_STORAGE_KEY = `med|filters`;

const stripUndefined = (obj: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

export function useFilters<T extends Record<string, unknown>>(
  initialValue: T,
  {
    replace = false,
    withPagination = true,
    withLocation = true,
    withLocalStorage = true,
    parser,
    onChange,
    scope,
  }: FilterPropsOptions<T>,
): FilterProps<T> {
  const location = useLocation();
  const { previous: previousLocation, historyAction } = useLocationCtx();

  const matches = useMatches();
  const filterScope = useMemo(() => scope || matches?.[1]?.pathname || '', [scope, matches]);
  const [searchParams, setSearchParams] = useSearchParams();
  const allSearchParams = useMemo(() => RouterUtils.decodeSearchParams(searchParams), [searchParams]);
  const [storedFilters, setStoredFilters] = useLocalStorage<StoredFilters<T> | null>({
    getInitialValueInEffect: false,
    key: FILTER_STORAGE_KEY,
    defaultValue: null,
  });

  const [filters, setFilterValues] = useState<T>(parser({ ...initialValue, ...(withLocation ? allSearchParams : {}) }));

  // Always keep a ref to the latest searchParams to avoid stale closure issues in callbacks
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // biome-ignore lint/correctness/useExhaustiveDependencies: updateFilters is a hook callback, always defined before effect runs
  useEffect(() => {
    if (!withLocalStorage) {
      return;
    }

    if (storedFilters) {
      const stored = storedFilters[location.pathname];

      if (!stored) {
        return;
      }

      if (ObjectUtils.objectsEqual(stripUndefined(stored?.data || {}), stripUndefined(filters))) {
        return;
      }

      updateFilters(
        {
          ...filters,
          ...parser(stored?.data || {}),
        },
        false,
      );
    }
  }, [storedFilters, filters, location.pathname, parser, withLocalStorage]);

  // Save Filters in Local Storage
  const saveFilters = useCallback(
    (nextFilter: NextFilters<T>) => {
      if (!withLocalStorage) {
        return;
      }

      setStoredFilters(prevValue => ({
        ...prevValue,
        ...{
          [location.pathname]: {
            path: location.pathname,
            data: nextFilter,
          },
        },
      }));
    },
    [setStoredFilters, withLocalStorage, location.pathname],
  );

  // Update the search parameters to include the filters
  const changeSearch = useCallback(
    (nextFilter: NextFilters<T>, replaceSearch?: boolean) => {
      if (!withLocation) {
        return;
      }

      const encoded = RouterUtils.encodeSearchParams(nextFilter);
      // Use ref to always compare against the latest URL, even in stale closures
      if (encoded.toString() === searchParamsRef.current.toString()) {
        return;
      }

      setSearchParams(encoded, { replace: replaceSearch });
    },
    [setSearchParams, withLocation],
  );

  // Update multiple filters value at the same time
  const updateFilters = useCallback(
    (nextFilter: NextFilters<T> = initialValue, resetPage = true, replaceSearch = replace) => {
      const newFilter = {
        ...initialValue,
        ...parser({
          ...nextFilter,
          ...(withPagination && resetPage && { page: 1 }),
        }),
      };
      saveFilters(newFilter);
      setFilterValues(newFilter);
      changeSearch(newFilter, replaceSearch);
      onChange?.(filters, newFilter, resetPage);
    },
    [initialValue, replace, parser, withPagination, saveFilters, changeSearch, onChange, filters],
  );

  // Remove a single filter value
  const removeFilter = useCallback(
    (key?: keyof T, resetPage = true) => {
      if (!key) {
        return updateFilters({ ...initialValue });
      }

      const tmpFilters = { ...filters };
      delete tmpFilters[key];
      updateFilters(tmpFilters, resetPage);
    },
    [initialValue, filters, updateFilters],
  );

  // Remove multiple filters value
  const removeMultipleFilters = useCallback(
    (keys?: (keyof T)[], resetPage = true) => {
      if (!keys) {
        return updateFilters({ ...initialValue });
      }

      const tmpFilters = { ...filters };

      keys.forEach(key => {
        delete tmpFilters[key];
      });

      updateFilters(tmpFilters, resetPage);
    },
    [initialValue, filters, updateFilters],
  );

  // Change a single filter value
  const changeFilter = useCallback(
    (key: keyof T, value: FilterValue = null, resetPage = true) => {
      updateFilters({ ...filters, [key]: value }, resetPage);
    },
    [filters, updateFilters],
  );

  // Clear all stored filers in the storage
  const clearSavedFilters = useCallback(() => {
    if (!withLocalStorage) {
      return;
    }

    setStoredFilters(null);
  }, [setStoredFilters, withLocalStorage]);

  // Reset the filter to initial values
  const resetFilters = useCallback(() => {
    const resetValue = { ...initialValue, ...parser({ ...initialValue }) };
    // Persist the reset value to storage too, the same way updateFilters does for every other
    // mutation - otherwise the storedFilters-sync effect above still holds the previous value and
    // re-merges it back over this reset on the very next render.
    saveFilters(resetValue);
    setFilterValues(resetValue);
    if (!withLocation) {
      return;
    }

    setSearchParams(createSearchParams(), { replace: true });
  }, [initialValue, parser, saveFilters, withLocation, setSearchParams]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialValue and parser are stable defaults, not reactive - only navigation events (allSearchParams, historyAction) should trigger POP restoration
  useEffect(() => {
    if (!withLocation || historyAction !== 'POP') {
      return;
    }

    const newFilters = { ...initialValue, ...allSearchParams };
    saveFilters(newFilters);
    setFilterValues(parser({ ...initialValue, ...allSearchParams }));
  }, [allSearchParams, historyAction, saveFilters, withLocation]);

  // Clear all filters on reload so we always prioritize URL
  useBeforeUnload(
    useCallback(() => {
      clearSavedFilters();
    }, [clearSavedFilters]),
  );

  // When navigating away from the page,
  // if the next doesn't match our scope, clear the stored filter
  useEffect(() => {
    if (previousLocation && historyAction) {
      if (historyAction === 'POP') {
        return;
      }

      if (!previousLocation.pathname.includes(filterScope)) {
        setStoredFilters(null);
      }
    }
  }, [filterScope, historyAction, previousLocation, setStoredFilters]);

  return {
    current: filters,
    updateFilters,
    removeFilter,
    removeMultipleFilters,
    changeFilter,
    resetFilters,
  };
}
